import HorizontalAnnotationFlag from '../flags/HorizontalAnnotationFlag';
import Indicator from '../Indicator';
import subpixel from '../util/canvasSubpixelOffset';

/** Horizontal Annotation indicator */
export default class HorizontalAnnotationIndicator extends Indicator {
	/** @param {object} args Custom args */
	constructor(args) {
		super(args);
		/**
		 * @type {object}
		 * @property {boolean} hover Is the mouse hovering over this indicator
		 */
		this.state = {
			hover: false
		};
	}

	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {string} text - Annotation text
	 * @property {string} textHover - Annotation text to display on hover
	 * @property {number} value - Annotation price value
	 * @property {boolean} noClip - Don't clip the annotation
	 * @property {object[]} inputs - Indicator inputs
	 */
	_getDefaultParams() {
		return {
			id: 'horizontalannotation',
			name: 'Horizontal Annotation',
			description: 'This indicator allows you to add a horizontal line to the chart.',
			text: 'Text',
			textHover: 'Hover Text',
			value: null,
			noClip: true,
			inputs: [{
				name: 'annotationValue',
				value: null
			}],
			style: this.core.getStyle('indicator.horizontalannotation')
		};
	}

	/** @returns {null} */
	getRangeMin() {
		return null;
	}

	/** @returns {null} */
	getRangeMax() {
		return null;
	}

	/** @returns {object} */
	_getMarkers() {
		return {};
	}

	/** @returns {object} */
	_getDataMap() {
		return {};
	}

	/**
	 * Returns true if mouse coordinates intersect indicator
	 * @param {number[]} mouse - Mouse [x, y] coordinates
	 * @returns {boolean}
	 */
	isWithin(mouse) {
		const { panel } = this;
		const mousePanel = [mouse[0], mouse[1] - panel.size.top];
		const style = this.params.style;
		const margin = this.params.style.margin;
		const value = this.getValue();
		const y0 = this.yScale(value);
		const left = panel.size.padding.left + margin;
		const mouseRect = {
			x: mousePanel[0],
			y: mousePanel[1],
			width: 1,
			height: 1
		};
		const collidesBottom = y0 > panel.size.height - margin - style.height;
		let ySnap = Math.min(panel.size.height - margin, Math.max(margin, y0));

		if (collidesBottom) {
			ySnap = panel.size.height - margin - style.height;
		}

		const boundingBox = {
			x: left,
			y: ySnap - style.height / 2,
			width: this.panel.size.width,
			height: style.height
		};

		return this._intersect(boundingBox, mouseRect);
	}

	/**
	 * Returns the value of this annotation
	 * @returns {number}
	 */
	getValue() {
		/** @type {number} */
		this.value = this.params.inputs[0].value || this.params.value;
		this.params.inputs[0].value = this.value; // just to make sure they're in sync

		if (this.panel.isNormalized) {
			return this.panel.actualToNormalized(this.value);
		} else {
			return this.value;
		}
	}

	/** @returns {undefined} */
	render() {
		const { panel } = this;
		const { style } = this.params;
		const { margin } = style;
		const value = this.getValue();
		const y0 = this.yScale(value);
		let left = panel.size.padding.left;
		const right = left + panel.size.width;
		const ctx = panel.rootContext;
		let ySnap = Math.min(panel.size.height - margin, Math.max(margin, y0));


		// create/update/delete flag
		if (!isNaN(value)) {
			if (this.flags['horizontalAnnotation']) {
				this.flags['horizontalAnnotation'].render(value);
			} else {
				this.flags[
					'horizontalAnnotation'
				] = new HorizontalAnnotationFlag({
					panel,
					indicator: this,
					id: 'horizontalAnnotation'
				});
			}
		} else if (this.flags['horizontalAnnotation']) {
			this.flags['horizontalAnnotation'].remove();
			delete this.flags['horizontalAnnotation'];
		} else {
			return; // bad value results in no further attempts to draw
		}

		ctx.beginPath();

		ctx.font = [
			style.text.font.weight,
			`${style.text.font.size}px`,
			style.text.font.family
		].join(' ');

		const textWidth = ctx.measureText(this.params.text).width;
		const textHeight = Math.round(ctx.measureText('M').width) + 0.5; // http://stackoverflow.com/questions/1134586/how-can-you-find-the-height-of-text-on-an-html-canvas;

		let yMid = Math.round(ySnap + style.height / 2 - textHeight / 2);
		const collidesTop = y0 < margin;
		const collidesBottom = y0 > panel.size.height - margin - style.height;

		ctx.textBaseline = 'top';

		// line
		if (!collidesTop && !collidesBottom) {
			ctx.lineWidth = this.params.style.width;
			ctx.strokeStyle = this.params.style.color;
			subpixel.moveTo(ctx, left, y0);
			subpixel.lineTo(ctx, right, y0);
			ctx.stroke();
		}

		// regular text label
		if (this.params.text && this.params.text.length) {
			left += margin;

			if (collidesBottom) {
				ySnap = panel.size.height - margin - style.height;
				yMid = Math.round(ySnap + style.height / 2 - textHeight / 2);
			}

			ctx.beginPath();
			ctx.textBaseline = 'top';

			// rect background
			ctx.fillStyle = this.params.style.text.background.color;
			subpixel.rect(ctx, left, ySnap, style.height, style.height);
			ctx.fill();

			ctx.beginPath();
			ctx.textBaseline = 'top';

			// text
			this.panel.glowText(
				ctx,
				this.params.text,
				left + style.height / 2 - textWidth / 2,
				yMid,
				this.params.style.text.color
			);

			// hover text
			if (this.state.hover) {
				// temp

				left += style.height;

				ctx.font = panel.getFontStyle(
					style.text.hover.font.weight,
					style.text.hover.font.size,
					style.text.hover.font.family
				);

				const textHoverWidth = ctx.measureText(this.params.textHover)
					.width;

				ctx.textBaseline = 'top';

				// rect background
				ctx.beginPath();
				ctx.fillStyle = this.params.style.text.hover.background.color;
				subpixel.rect(
					ctx,
					left,
					ySnap,
					textHoverWidth + 10,
					style.height + 2
				);
				ctx.fill();

				ctx.beginPath();

				left += 5;

				// hover text
				this.panel.glowText(
					ctx,
					this.params.textHover,
					left,
					yMid,
					this.params.style.text.hover.color
				);
			}
		}
	}

	/**
	 * Gets generic object representation of indicator -> Not to be exported
	 * don't save certain indicators // see NXC-581
	 * @returns {undefined}
	 */
	toJSON() {
		return undefined;
	}
}
